'use client'

import { useMemo, useState, type ReactNode } from 'react'
import Link from 'next/link'
import { toast } from 'sonner'
import { BellOff, CheckCheck, ChevronRight, Volume2, VolumeX } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@elmnts/ui/popover'
import { Button } from '@elmnts/ui/button'
import { useNotificationsOverview } from '@/hooks/notifications/useNotificationsQuery'
import { useNotificationActions } from '@/hooks/notifications/useNotificationActions'
import { useNotificationPrefs } from '@/hooks/notifications/useNotificationPrefs'
import { cn } from '@/lib/utils'
import type { NotificationKind } from '@/services/notifications/types'
import { Notification } from './index'
import { KIND_STYLES, compareByPriority } from './kind-style'
import { NotificationListSkeleton } from './NotificationCardSkeleton'

const PREVIEW_LIMIT = 3

/** Peek das novas, ordenadas por severidade; o resto fica na página. */
export function NotificationPopover({ children }: { children: ReactNode }) {
  const { data, isLoading } = useNotificationsOverview()
  const { markRead } = useNotificationActions()
  const { prefs, setSoundEnabled } = useNotificationPrefs()
  const [open, setOpen] = useState(false)

  const sorted = useMemo(() => [...(data?.new ?? [])].sort(compareByPriority), [data?.new])
  const visible = sorted.slice(0, PREVIEW_LIMIT)
  const hidden = sorted.length - visible.length

  const kindCounts = useMemo(() => {
    const acc: Partial<Record<NotificationKind, number>> = {}
    for (const n of sorted) acc[n.kind] = (acc[n.kind] ?? 0) + 1
    return acc
  }, [sorted])

  const handleMarkAllRead = () => {
    if (!sorted.length) return
    markRead.mutate(
      { ids: sorted.map((n) => n.id) },
      {
        onSuccess: () => toast.success('Todas marcadas como lidas'),
        onError: () => toast.error('Não foi possível marcar como lidas'),
      },
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" side="right" sideOffset={12} className="w-96 overflow-hidden p-0">
        <div className="border-b border-border bg-gradient-to-r from-primary/5 to-transparent px-3 py-2.5">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold">Notificações</div>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="size-7"
                onClick={() => setSoundEnabled(!prefs.soundEnabled)}
                aria-label={prefs.soundEnabled ? 'Desativar som' : 'Ativar som'}
              >
                {prefs.soundEnabled ? (
                  <Volume2 className="size-3.5" />
                ) : (
                  <VolumeX className="size-3.5 text-muted-foreground" />
                )}
              </Button>
              {sorted.length > 0 && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="size-7"
                  onClick={handleMarkAllRead}
                  disabled={markRead.isPending}
                  aria-label="Marcar todas como lidas"
                  title="Marcar todas como lidas"
                >
                  <CheckCheck className="size-3.5" />
                </Button>
              )}
            </div>
          </div>
          {Object.keys(kindCounts).length > 0 && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1">
              {(Object.entries(kindCounts) as [NotificationKind, number][]).map(([k, n]) => {
                const style = KIND_STYLES[k]
                const Icon = style.icon
                return (
                  <span
                    key={k}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium',
                      style.borderClass,
                      style.iconClass,
                    )}
                  >
                    <Icon className="size-2.5" />
                    {n}
                  </span>
                )
              })}
            </div>
          )}
        </div>

        <div
          className="space-y-2 p-3"
          // Clique num card navega: fecha o popover pra não cobrir o destino.
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('a')) setOpen(false)
          }}
        >
          {isLoading && <NotificationListSkeleton count={2} />}
          {!isLoading && visible.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-8 text-center text-muted-foreground">
              <BellOff className="size-6" />
              <div className="text-sm font-medium">Tudo em dia</div>
              <div className="text-xs">Sem notificações novas</div>
            </div>
          )}
          {visible.map((n) => (
            <Notification.Preview key={n.id} notification={n} href={`/notifications/${n.id}`} />
          ))}
        </div>

        <Link
          href="/notifications"
          onClick={() => setOpen(false)}
          className="flex items-center justify-between border-t border-border px-3 py-2.5 text-sm font-medium hover:bg-accent/50"
        >
          <span className="inline-flex items-center gap-1">
            {hidden > 0 ? (
              <>
                + {hidden} {hidden === 1 ? 'notificação' : 'notificações'}
              </>
            ) : (
              <>Ver todas</>
            )}
          </span>
          <ChevronRight className="size-4 text-muted-foreground" />
        </Link>
      </PopoverContent>
    </Popover>
  )
}
